import * as THREE from 'three';
import { Vec3 } from '@/core/math/Vec3';
import { CelestialBody } from '@/universe/CelestialBody';
import { SolarSystem } from '@/universe/SolarSystem';
import { CelestialBodyDef, isStarDef } from '@/universe/BodyDef';
import { clamp01, smoothstep } from '@/core/math/scalar';
import { hashString, toUnit } from '@/core/math/hash';
import { GLSL_CLOUD_FIELD, GLSL_CONSTANTS, GLSL_NOISE, GLSL_RING_DENSITY } from './shaders/common.glsl';
import { SUN_COLOR, SUN_INTENSITY_DEFAULT } from './shaders/atmosphereDefaults';
import { PlanetRingSystem } from './PlanetRingSystem';
import { DistantBodyPoints } from './DistantBodyPoints';

/**
 * Scaled astronomical rendering.
 *
 * Distant bodies are drawn at *compressed* distances. A planet 5 AU away is not
 * placed 7.5e11 render units away; it is placed a few thousand units away with
 * its radius scaled by exactly the same factor.
 *
 *     renderDistance = k * log10(1 + trueDistance / 1e6)
 *     renderRadius   = trueRadius * renderDistance / trueDistance
 *
 * Because the radius is scaled by the same ratio as the distance, the *angular
 * size is preserved exactly*. Direction is preserved exactly. The mapping is
 * strictly monotonic in true distance, so occlusion order is preserved too. The
 * player cannot tell the difference, and the depth buffer only has to resolve a
 * few thousand units instead of ten trillion.
 *
 * Bodies with real terrain streamed in — see `Game.updateActiveBodySet`, which
 * can promote more than one at once (a planet and its moons, close enough to
 * matter together) rather than a single exclusive "active body" — are
 * excluded and drawn at true scale by the terrain system instead. Bodies hand
 * over between the two representations at a distance where the terrain's
 * coarsest LOD and this sphere are visually equivalent, and only once the
 * terrain side actually has geometry (`hiddenBodyIds`).
 */

const COMPRESSION_K = 1000;
const MIN_RENDER_DISTANCE = 30;
const COMPRESSION_DISTANCE_SCALE = 1e6; // trueDistance is divided by this before the log, metres
const EMPTY_SET: ReadonlySet<string> = new Set();

export function compressDistance(trueDistance: number): number {
  return Math.max(MIN_RENDER_DISTANCE, COMPRESSION_K * Math.log10(1 + trueDistance / COMPRESSION_DISTANCE_SCALE));
}

/** Below this true distance a body is effectively at the camera; nothing sensible to draw or divide by. */
const MIN_BODY_DISTANCE = 1e-6;
/** Floor on renderRadius so a very distant/small body never shrinks to an unclickable, invisible point. */
const MIN_RENDER_RADIUS = 0.02;
// The star lights itself, and does so far above white — see the uSunIntensity
// override in update(). Its disc has to clip as hard as the glare around it,
// or the two meet at a visible edge and the star reads as a pale ball sitting
// inside a separate halo instead of as one source too bright to look at.
const STAR_SELF_INTENSITY = 16;

// --- Naked-eye brightness of a distant body (see `update`) -------------------
//
// The impostor above is angularly exact, and that is precisely why a body at
// real interplanetary distance disappears: a sub-pixel disc is a sub-pixel
// disc. What makes Venus and Jupiter obvious in a real sky — and Pluto
// invisible — is received flux, so that is what is computed here and handed to
// `DistantBodyPoints` as a size and an intensity.
//
// Received flux from a body, W/m^2:
//
//   E = albedo * (L / (4*PI*ds^2)) * R^2 / d^2 * phase
//
// with `ds` the body's distance from the star, `R` its radius, `d` its distance
// from the camera and `phase` the sunlit fraction of the hemisphere facing the
// camera. Every constant factor a rigorous derivation would carry (the 1/4 of a
// Lambertian sphere's projected-to-total area ratio, the 1/PI of the BRDF) is
// deliberately folded into the magnitude reference below instead of written
// out: only the *ordering* and the *dynamic range* matter here, and pinning the
// zero point to one measured body in this system is a far more useful
// calibration than an exact prefactor multiplied by an authored albedo that was
// never meant to be photometric.
//
// Converted to a magnitude (the log scale eyes actually work on) and then to a
// 0..1 visibility, which drives both size and intensity:
//
//   mag = -2.5 * log10(E),  vis = (MAG_LIMIT - mag) / (MAG_LIMIT - MAG_FULL)
//
// Calibrated against this system's own data (`src/universe/data/`), computed at
// each planet's closest approach to Halcyon:
//
//   Thalassa  mag 13.0   vis 1.00   (7.0e6 m, 1.31 AU, disc albedo 0.33)
//   Ashkar    mag 13.4   vis 0.95   (6.0e6 m, 0.68 AU, 0.51 — this system's Venus)
//   Vesper    mag 13.4   vis 0.95   (5.2e6 m, 0.38 AU, 0.29)
//   Aegis     mag 16.0   vis 0.65   (6.4e7 m, 5.40 AU, 0.84 — the gas giant)
//   Orphys    mag 16.4   vis 0.60
//   Kharis    mag 16.6   vis 0.58
//   Meridia   mag 18.4   vis 0.36
//   Kolyma    mag 23.4   vis 0      (9.15 AU — this system's Pluto, below the limit)
//
// MAG_FULL is set at the brightest body's opposition so nothing saturates the
// scale, and MAG_LIMIT so the outermost world sits just under the threshold.
/** Fallback stellar luminosity, W, if the system's star carries none. */
const DEFAULT_STAR_LUMINOSITY = 4.2e26;
/** Apparent magnitude at which a body reaches full point brightness. */
const MAG_FULL = 13.0;
/** Apparent magnitude at which a body stops being drawn at all. */
const MAG_LIMIT = 21.5;
/** Flux below this is not worth a log; also guards log10(0). */
const MIN_POINT_FLUX = 1e-18;
/** Point-sprite diameter, device-independent pixels, at zero and full visibility. */
const POINT_SIZE_MIN_PX = 2.0;
const POINT_SIZE_RANGE_PX = 6.0;
/**
 * Radiance of the point before washout, and how hard it is spread across the
 * visibility range.
 *
 * The gain is set so the brightest body in the system saturates — Venus is the
 * brightest thing in a real night sky after the moon, and a "bright planet"
 * that merely matches the field stars around it is not one. The square then
 * pushes everything else down: without it the whole middle of the system came
 * out at a similar mid-grey, which reads as eight identical dots rather than as
 * a hierarchy. Measured on a real GPU from Halcyon's night side, the resulting
 * on-screen peaks run 0.47 (Ashkar) / 0.50 (Orphys) / 0.33 (Aegis) / 0.03
 * (Kharis) / 0 (Kolyma) before this pair was raised from 1.7 and 1.5.
 */
const POINT_INTENSITY_GAIN = 4.5;
const POINT_INTENSITY_POWER = 2.0;
/** How far a bright body's colour is pulled toward white — a bright point reads hot, a faint one keeps its hue. */
const POINT_WHITE_MIX = 0.6;
/**
 * Cross-fade band, in angular RADIUS (radians), between "this is a point of
 * light" and "this is a disc with structure on it".
 *
 * Below the first the impostor covers well under a pixel and carries no
 * information at all; above the second it is a couple of pixels across and
 * starts to read as a body, at which point a glow on top of it would be a
 * second, mismatched image of the same thing. Chosen in angle rather than in
 * pixels because the impostor's angular size is the one quantity the
 * compression preserves exactly; at the game's ~60-degree vertical field over
 * 720 rows these are about 0.5 and 2.3 pixels of radius.
 */
const POINT_ANGULAR_RADIUS = 8e-4;
const DISC_ANGULAR_RADIUS = 3.5e-3;
/** Lerp target for `POINT_WHITE_MIX`; module-level so the hot path allocates nothing. */
const WHITE = new THREE.Color(1, 1, 1);

interface BodyVisual {
  body: CelestialBody;
  mesh: THREE.Mesh;
  material: THREE.ShaderMaterial;
  rings: PlanetRingSystem | null;
  /**
   * Whole-disc reflectance as one number, and the hue to draw the point in.
   * Both derived once from `discAlbedo` — the bolometric quantity, clouds
   * included, which is exactly right here even though the *disc* shader
   * deliberately does not use it (see `discSurfaceColors`).
   */
  pointAlbedo: number;
  pointColor: THREE.Color;
  /** Last frame's photometry, mutated in place — see `AstronomicalLayer.photometry`. */
  photometry: BodyPhotometry;
}

/**
 * What the brightness model decided about one body this frame.
 *
 * Kept because "why is that world invisible" is otherwise unanswerable from
 * outside — the numbers below are the whole model, and reading them live
 * (`scripts/probe.mjs`) is how the constants above were calibrated in the first
 * place. One object per body, allocated at construction and mutated in place.
 */
export interface BodyPhotometry {
  /** Angular RADIUS of the real body from the camera, radians. */
  angularRadius: number;
  /** Sunlit fraction of the hemisphere facing the camera, 0..1. */
  phase: number;
  /** Received flux, W/m^2. */
  flux: number;
  /** Apparent magnitude, `-2.5 * log10(flux)`. */
  magnitude: number;
  /** 0..1 brightness from the magnitude, before the disc cross-fade. */
  visibility: number;
  /** 1 while the body is a point, 0 once its impostor reads as a disc. */
  pointFade: number;
}

const BODY_VERTEX = /* glsl */ `
varying vec3 vNormal;
varying vec3 vLocal;
varying vec3 vRenderPosition;
void main() {
  vNormal = normalize(mat3(modelMatrix) * normal);
  // Object space, which the mesh's own body-fixed rotation (see 'update')
  // makes the PLANET-FIXED direction. Every procedural field below is keyed to
  // this rather than to the world normal, so the continents, the weather and
  // the night lights turn with the planet instead of hanging in inertial space
  // while it spins underneath them.
  vLocal = normalize(normal);
  vec4 world = modelMatrix * vec4(position, 1.0);
  vRenderPosition = world.xyz;
  gl_Position = projectionMatrix * viewMatrix * world;
}
`;

const BODY_FRAGMENT = /* glsl */ `
precision highp float;

${GLSL_CONSTANTS}
${GLSL_RING_DENSITY}
${GLSL_NOISE}
${GLSL_CLOUD_FIELD}

varying vec3 vNormal;
varying vec3 vLocal;
varying vec3 vRenderPosition;

uniform vec3 uSunDirection;
uniform vec3 uSunColor;
uniform float uSunIntensity;
/** Star disc colour. Planets use the land/water/cloud terms below instead. */
uniform vec3 uAlbedo;
uniform vec3 uLandColor;
uniform vec3 uWaterColor;
uniform float uOceanCoverage;
uniform vec3 uCloudColor;
uniform float uCloudCoverage;
uniform float uCloudDrift;
uniform float uBanded;
uniform float uTime;
/**
 * Per-body offset into the procedural fields below.
 *
 * Without it every impostor in the system samples the same noise at the same
 * coordinates and therefore wears the SAME continents and the same night-light
 * clusters — two bodies side by side on screen are visibly one texture twice,
 * which is what a moon and its planet looked like before this existed.
 */
uniform vec3 uFieldOffset;
uniform vec3 uNightColor;
uniform float uNightStrength;
uniform float uEmissive;
uniform vec3 uRingNormal;
uniform float uRingInnerRatio;
uniform float uRingOuterRatio;
uniform float uRingOpacity;

// Star disc: limb darkening keeps it from reading as a flat circle.
const float STAR_EMISSIVE_THRESHOLD = 0.5; // uEmissive above this selects the star branch
const float LIMB_DARKENING_FLOOR = 0.42;
const float LIMB_DARKENING_GAIN = 0.58;
const float LIMB_DARKENING_POWER = 0.42;

// Coarse continent-like variation standing in for real terrain until this
// body is promoted (see the comment below).
const float CONTINENT_FREQ_PRIMARY = 2.2;
const float CONTINENT_FREQ_SECONDARY = 5.0;
const float CONTINENT_WEIGHT_PRIMARY = 0.75;
const float CONTINENT_WEIGHT_SECONDARY = 0.25;
// Where the continent threshold sits for an entirely dry world and for an
// entirely drowned one. The field is interpolated between these by the body's
// authored ocean coverage, so a 94%-ocean world like Thalassa shows a few
// islands rather than the half-and-half split a fixed mid threshold gives
// every planet regardless of how much water it actually has.
const float LAND_THRESHOLD_DRY = 0.30;
const float LAND_THRESHOLD_DROWNED = 0.78;
const float LAND_EDGE_LOW = 0.06;
const float LAND_EDGE_HIGH = 0.10;
// Relief variation within the land mask, so continents are not one flat tone.
const float LAND_ALBEDO_DARK = 0.82;
const float LAND_ALBEDO_LIGHT = 1.14;
/**
 * How opaque the impostor's cloud deck gets at full field density. The real
 * deck is a ray-marched volume with its own scattering; this is a flat
 * composite over the surface, so it stops short of fully hiding the ground
 * for the same reason DISC_CLOUD_COVERAGE_CAP does — a broken deck shows
 * water through it.
 */
const float CLOUD_MAX_OPACITY = 0.92;

// Terminator softness (wrapped Lambert) and how much it blends against a
// hard NdotL.
const float TERMINATOR_WRAP_BIAS = 0.12;
const float TERMINATOR_WRAP_RANGE = 1.12;
const float TERMINATOR_WRAP_MIX = 0.5;

// Night-glow gate: dot(N, sunDirection) range over which night emission
// ramps in. Not the same band as TerrainMaterial's (this impostor has no
// atmosphere to soften its own terminator), only the cluster/grain SHAPING
// past the gate is shared — see NIGHT_GLOW_* in GLSL_CONSTANTS.
const float NIGHT_FACTOR_SMOOTH_START = 0.02;
const float NIGHT_FACTOR_SMOOTH_END = -0.28;
const float NIGHT_FACTOR_THRESH = 0.001;
const float NIGHT_GLOW_FREQ_PRIMARY = 9.0;
const float NIGHT_GLOW_FREQ_SECONDARY = 26.0;
const float NIGHT_GRAIN_FREQ = 40.0;

void main() {
  vec3 N = normalize(vNormal);
  vec3 L = normalize(uSunDirection);
  vec3 V = normalize(cameraPosition - vRenderPosition);

  if (uEmissive > STAR_EMISSIVE_THRESHOLD) {
    // Self-luminous body (the star). Limb darkening keeps the disc from
    // reading as a flat circle.
    float mu = max(dot(N, V), 0.0);
    float limb = LIMB_DARKENING_FLOOR + LIMB_DARKENING_GAIN * pow(mu, LIMB_DARKENING_POWER);
    gl_FragColor = vec4(uAlbedo * limb * uSunIntensity, 1.0);
    // This shader has two exits and both need encoding; see TerrainMaterial.
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
    return;
  }

  // Planet-fixed sample direction, renormalised: an interpolated unit vector
  // is shorter mid-triangle than at the corners, and feeding the raw varying
  // to noise paints the sphere's own tessellation across every field below.
  vec3 local = normalize(vLocal);

  // Coarse continent-like variation. A single flat albedo disc is exactly what
  // the real, promoted terrain never shows — even its coarsest resident chunk
  // carries 'ChunkBuilder.surfaceColor''s elevation-banded palette, which is
  // what makes a planet read as itself "from orbit". This has no heightfield to
  // band, so a low-frequency fbm stands in for continents. It will not line up
  // with the real terrain once promoted, but land and sea are now the body's
  // OWN land and sea colours in roughly its own proportion, rather than one
  // averaged tone brightened and darkened.
  float continent = fbm(local * CONTINENT_FREQ_PRIMARY + uFieldOffset, 4) * CONTINENT_WEIGHT_PRIMARY
                   + fbm(local * CONTINENT_FREQ_SECONDARY + uFieldOffset, 3) * CONTINENT_WEIGHT_SECONDARY;
  float landThreshold = mix(LAND_THRESHOLD_DRY, LAND_THRESHOLD_DROWNED, uOceanCoverage);
  float landMix = smoothstep(landThreshold - LAND_EDGE_LOW, landThreshold + LAND_EDGE_HIGH, continent);
  vec3 land = uLandColor * mix(LAND_ALBEDO_DARK, LAND_ALBEDO_LIGHT, continent);
  vec3 albedo = mix(uWaterColor, land, landMix);

  // Weather, from the SAME field and the same drift, coverage and banding the
  // real cloud deck marches (see GLSL_CLOUD_FIELD). Sampling one field means
  // the fronts sit in the same places either side of the hand-off instead of
  // the impostor carrying a whitened average of a deck it never draws.
  if (uCloudCoverage > 0.0) {
    float cloudDetail;
    float cloud = cloudDensityAt(local, uTime, uCloudDrift, uCloudCoverage, uBanded, cloudDetail);
    albedo = mix(albedo, uCloudColor, cloud * CLOUD_MAX_OPACITY);
  }

  float NdotL = max(dot(N, L), 0.0);
  // Lambert with a soft terminator, which is what a body with any atmosphere
  // actually shows.
  float wrapped = clamp((dot(N, L) + TERMINATOR_WRAP_BIAS) / TERMINATOR_WRAP_RANGE, 0.0, 1.0);
  float ringShadow = ringShadowAtPoint(
    N,
    L,
    uRingNormal,
    uRingInnerRatio,
    uRingOuterRatio,
    uRingOpacity
  );
  // Divided by PI, exactly as 'shadeSurface' does for the real terrain.
  // Without it this disc was PI times brighter than the same albedo lit the
  // same way on the promoted surface — measured at 0.655 against 0.209 for
  // Thalassa — which at the usual exposure tone maps to very nearly white and
  // is what made a dark blue ocean world hand off to a featureless pale ball.
  vec3 color = albedo / PI * uSunColor * uSunIntensity
    * mix(NdotL, wrapped, TERMINATOR_WRAP_MIX) * (1.0 - ringShadow);

  // Night side: city lights, lava glow. Patchy and faint.
  //
  // A flat wash of the emissive colour across the whole unlit hemisphere is
  // what a constant term produces, and on a volcanic world with a strong glow
  // that is a solid orange half-disc with a hard edge down the terminator —
  // which is not a planet at night, it is a coloured shape. Real night-side
  // emission is concentrated in a small part of the surface, so it is broken up
  // by noise and kept well below the daylight side.
  //
  // Shaped the same way as 'TerrainMaterial''s night glow (cube of a narrow
  // cluster threshold, times a finer grain field) rather than a single
  // mid-range smoothstep: the single-field version passes roughly a third of
  // the surface through a soft ramp and reads as regions of ground quietly
  // luminous, not as discrete lights — the same failure 'TerrainMaterial'
  // already worked around, and worth matching here so a body does not visibly
  // change how its night side looks the moment it hands off to real terrain.
  float night = smoothstep(NIGHT_FACTOR_SMOOTH_START, NIGHT_FACTOR_SMOOTH_END, dot(N, L));
  if (night > NIGHT_FACTOR_THRESH && uNightStrength > 0.0) {
    float glowField = fbm(local * NIGHT_GLOW_FREQ_PRIMARY + uFieldOffset, 4) * NIGHT_GLOW_PRIMARY_WEIGHT
                     + fbm(local * NIGHT_GLOW_FREQ_SECONDARY + uFieldOffset, 3) * NIGHT_GLOW_SECONDARY_WEIGHT;
    float cluster = smoothstep(NIGHT_GLOW_CLUSTER_START, NIGHT_GLOW_CLUSTER_END, glowField);
    float grain = smoothstep(NIGHT_GLOW_GRAIN_START, NIGHT_GLOW_GRAIN_END, fbm(local * NIGHT_GRAIN_FREQ + uFieldOffset, 3));
    float emission = cluster * cluster * cluster * grain;
    color += uNightColor * uNightStrength * night * emission;
  }

  gl_FragColor = vec4(color, 1.0);

  // See TerrainMaterial.
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

/**
 * Whole-disc albedo of a body seen from astronomical distance.
 *
 * The obvious choice — the palette's mid-elevation colour — is the wrong
 * quantity twice over. It is one elevation band out of seven, and it ignores
 * the two things that dominate what a planet actually reflects: how much of it
 * is ocean, and how much of it is under cloud. A world like Halcyon is 62%
 * water and 52% cloud, so a disc lit by its mid-elevation rock colour comes out
 * several times too dark and reads as unlit rather than as a distant planet.
 *
 * Composing it from ocean coverage, terrain colours and cloud coverage puts
 * every body somewhere sensible on its own: cloud-covered Ashkar is brilliant,
 * the ocean world is dark blue with bright weather over it, and an airless moon
 * is exactly its rock.
 */
/** How far the disc's land tone is pulled from midland toward highland. */
const DISC_LAND_HIGHLAND_MIX = 0.4;
/**
 * Cloud does not cover completely even where it is present: broken deck,
 * gaps and thin edges all show the ground through, so full authored coverage
 * still lerps only this far toward the cloud colour.
 */
const DISC_CLOUD_COVERAGE_CAP = 0.85;
/** How far the impostor's water tone is pulled from the shelf toward the abyss. */
const DISC_WATER_DEEP_MIX = 0.55;

/**
 * The impostor's land and water tones, kept SEPARATE rather than averaged.
 *
 * `discAlbedo` above is the whole-disc bolometric reflectance, which is the
 * right quantity for planetshine — a neighbour lighting your night side does
 * so with one averaged colour, clouds included. It is the wrong quantity for a
 * disc the player is looking straight at, and it was being used for both: on
 * Thalassa the cloud term alone contributed 0.2975 of a 0.314 albedo, so 95% of
 * the impostor's red channel was cloud and a 94%-ocean world rendered as
 * neutral grey. The shader composites its own clouds from the shared field
 * now, so what it needs from here is the ground underneath them.
 */
export interface DiscSurfaceColors {
  land: THREE.Color;
  water: THREE.Color;
  oceanCoverage: number;
}

export function discSurfaceColors(def: CelestialBodyDef): DiscSurfaceColors {
  const p = def.palette;
  return {
    land: new THREE.Color(p.midland[0], p.midland[1], p.midland[2]).lerp(
      new THREE.Color(p.highland[0], p.highland[1], p.highland[2]),
      DISC_LAND_HIGHLAND_MIX,
    ),
    // Shallow water, not deep: the deep tone is what the abyss reads as from
    // the surface, and a disc seen whole is dominated by the lit shelf and by
    // the scattering above it. Blended, so a world with a real abyss still
    // reads darker than one without.
    water: new THREE.Color(p.shallowWater[0], p.shallowWater[1], p.shallowWater[2]).lerp(
      new THREE.Color(p.deepWater[0], p.deepWater[1], p.deepWater[2]),
      DISC_WATER_DEEP_MIX,
    ),
    oceanCoverage: def.terrain ? clamp01(def.terrain.oceanCoverage) : 0,
  };
}

export function discAlbedo(def: CelestialBodyDef): THREE.Color {
  const p = def.palette;
  const land = new THREE.Color(p.midland[0], p.midland[1], p.midland[2]).lerp(
    new THREE.Color(p.highland[0], p.highland[1], p.highland[2]),
    DISC_LAND_HIGHLAND_MIX,
  );
  const water = new THREE.Color(p.deepWater[0], p.deepWater[1], p.deepWater[2]);

  const ocean = def.terrain ? clamp01(def.terrain.oceanCoverage) : 0;
  const surface = land.lerp(water, ocean);

  const atm = def.atmosphere;
  if (!atm || atm.cloudCoverage <= 0) return surface;

  const cloud = new THREE.Color(atm.cloudColor[0], atm.cloudColor[1], atm.cloudColor[2]);
  return surface.lerp(cloud, clamp01(atm.cloudCoverage) * DISC_CLOUD_COVERAGE_CAP);
}

/** Rec. 709 relative luminance — the single number "how much light does this reflect". */
function luminanceOf(c: THREE.Color): number {
  return 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b;
}

export class AstronomicalLayer {
  readonly root = new THREE.Group();
  /**
   * Impostors positioned at TRUE scale/distance instead of the compressed
   * mapping, for bodies close enough to a promoted sibling that the two might
   * occlude each other on screen. Added to the world scene by `Game`, not the
   * astronomical scene — see `update`'s `nearGroupIds` parameter for why.
   */
  readonly nearRoot = new THREE.Group();
  private visuals = new Map<string, BodyVisual>();
  private readonly sphere: THREE.SphereGeometry;
  private readonly scratch = new Vec3();
  /**
   * Bodies too far to resolve, drawn as points of light instead — see
   * `DistantBodyPoints`. Parented into `root` so it shares the astronomical
   * pass and the `distantBodies` debug switch with the impostors.
   */
  private readonly points: DistantBodyPoints;
  private readonly starLuminosity: number;
  private readonly pointColor = new THREE.Color();
  /** Last frame's brightness model per body id — see `BodyPhotometry`. */
  readonly photometry = new Map<string, BodyPhotometry>();

  /**
   * Bodies currently rendered at true scale by the terrain system, and so
   * hidden here instead. A body enters this set only once its real terrain
   * actually has geometry resident (`PlanetSurface.rootsReady`) — hiding the
   * impostor any earlier leaves a frame with nothing solid drawn where the
   * body should be, and whatever sits behind it on screen — typically another
   * body's own impostor — shows through the gap.
   *
   * The set is the *intent* to hide; `update` still re-checks each body's
   * distance against the world passes' far plane (`nearCutoffDistance`). A
   * promoted body whose real terrain sits beyond that plane cannot be drawn at
   * all — most visibly a parent planet promoted to real terrain while the
   * camera stands on its moon, where the far plane is sized to the moon's
   * radius and the planet is several times farther out. Hiding the impostor
   * there leaves nothing where the body should be, so it stays on the
   * compressed path until the far plane actually reaches it.
   */
  hiddenBodyIds: ReadonlySet<string> = new Set();

  constructor(
    private readonly system: SolarSystem,
    segments = 48,
  ) {
    this.sphere = new THREE.SphereGeometry(1, segments, segments / 2);
    this.root.frustumCulled = false;
    this.nearRoot.frustumCulled = false;

    for (const body of system.bodies) {
      this.visuals.set(body.id, this.createVisual(body));
    }

    const starDef = system.star.def;
    this.starLuminosity = isStarDef(starDef) ? starDef.luminosity : DEFAULT_STAR_LUMINOSITY;
    this.points = new DistantBodyPoints(system.bodies.length);
    this.root.add(this.points.object);
  }

  setPixelRatio(ratio: number): void {
    this.points.setPixelRatio(ratio);
  }

  /** See `DistantBodyPoints.setWashout` — share the starfield's own factor. */
  setAtmosphericWashout(brightness: number): void {
    this.points.setWashout(brightness);
  }

  private createVisual(body: CelestialBody): BodyVisual {
    const def = body.def;
    const star = isStarDef(def);
    const albedo = star
      ? new THREE.Color(def.color[0], def.color[1], def.color[2])
      : discAlbedo(def);

    const surface = discSurfaceColors(def);
    const atm = def.atmosphere;
    const material = new THREE.ShaderMaterial({
      uniforms: {
        uSunDirection: { value: new THREE.Vector3(1, 0, 0) },
        uSunColor: { value: new THREE.Color(...SUN_COLOR) },
        uSunIntensity: { value: SUN_INTENSITY_DEFAULT },
        uAlbedo: { value: albedo },
        uLandColor: { value: surface.land },
        uWaterColor: { value: surface.water },
        uOceanCoverage: { value: surface.oceanCoverage },
        // Matched to CloudLayer's own uniforms so both sample one field at one
        // speed: a body's weather is in the same place before and after the
        // hand-off, not two unrelated patterns.
        uCloudColor: {
          value: atm
            ? new THREE.Color(atm.cloudColor[0], atm.cloudColor[1], atm.cloudColor[2])
            : new THREE.Color(1, 1, 1),
        },
        uCloudCoverage: { value: star ? 0 : (atm?.cloudCoverage ?? 0) },
        uCloudDrift: { value: (atm?.windSpeed ?? 0) / Math.max(1, body.radius) },
        uBanded: { value: def.kind === 'gasGiant' ? 1 : 0 },
        uTime: { value: 0 },
        // Deterministic per body, so a world looks the same every session but
        // never the same as its neighbour. The cloud field deliberately takes
        // NO offset: it is shared with the real deck on purpose, and coverage
        // already differs per body (an airless moon has none at all).
        uFieldOffset: {
          value: new THREE.Vector3(
            toUnit(hashString(`${def.id}:continent`)) * 64,
            toUnit(hashString(`${def.id}:glow`)) * 64,
            toUnit(hashString(`${def.id}:grain`)) * 64,
          ),
        },
        uNightColor: {
          value: new THREE.Color(
            def.palette.nightEmissive[0],
            def.palette.nightEmissive[1],
            def.palette.nightEmissive[2],
          ),
        },
        uNightStrength: { value: def.palette.nightEmissiveStrength },
        uEmissive: { value: star ? 1 : 0 },
        uRingNormal: { value: new THREE.Vector3(body.spinAxis.x, body.spinAxis.y, body.spinAxis.z) },
        uRingInnerRatio: { value: def.rings ? def.rings.innerRadius / body.radius : 0 },
        uRingOuterRatio: { value: def.rings ? def.rings.outerRadius / body.radius : 0 },
        uRingOpacity: { value: def.rings?.opacity ?? 0 },
      },
      vertexShader: BODY_VERTEX,
      fragmentShader: BODY_FRAGMENT,
      depthWrite: true,
      depthTest: true,
    });

    const mesh = new THREE.Mesh(this.sphere, material);
    mesh.frustumCulled = false;
    mesh.name = `astro:${body.id}`;
    this.root.add(mesh);

    let rings: PlanetRingSystem | null = null;
    if (def.rings) {
      rings = new PlanetRingSystem(body, 192);
      // Rings lie in the body's equatorial plane.
      rings.mesh.rotation.x = Math.PI / 2 + def.axialTilt;
      rings.mesh.renderOrder = 1;
      this.root.add(rings.mesh);
    }

    // The point's own colour and reflectance. Kept separate from `albedo`
    // above, which for the star is its emission colour rather than an albedo at
    // all — the star never gets a point (see `update`), but the field would be
    // wrong if it ever did.
    const disc = star ? new THREE.Color(1, 1, 1) : discAlbedo(def);
    const pointAlbedo = star ? 1 : luminanceOf(disc);
    // Normalised to unit peak: how *dark* the body is already enters through
    // `pointAlbedo` in the flux, so leaving it in the colour would charge it
    // twice and turn every low-albedo world black rather than faint.
    const peak = Math.max(disc.r, disc.g, disc.b, 1e-4);
    const pointColor = disc.clone().multiplyScalar(1 / peak);

    const photometry: BodyPhotometry = {
      angularRadius: 0,
      phase: 0,
      flux: 0,
      magnitude: Infinity,
      visibility: 0,
      pointFade: 0,
    };
    this.photometry.set(body.id, photometry);

    return { body, mesh, material, rings, pointAlbedo, pointColor, photometry };
  }

  /**
   * Positions every body for this frame.
   *
   * `cameraPositionRoot` is the camera's position in the root (system inertial)
   * frame, in metres.
   *
   * `nearGroupIds` names bodies sharing a promoted sibling's local group (see
   * `Game.terrainGroupOf`) that are not themselves promoted — typically a
   * moon just outside its planet's promotion range while the planet itself
   * already has real terrain on screen. Left on the default compressed path,
   * such a body is ALWAYS drawn behind the promoted sibling regardless of
   * which one is actually nearer: the astronomical pass runs before the world
   * passes and depth is cleared in between, so the world pass's terrain
   * paints over it unconditionally. There is no such bug the other way, and
   * there never can be one between two bodies that are both compressed —
   * `compressDistance` is monotonic, so ordering among astronomical bodies is
   * correct on its own; it is only the hand-off to real, true-scale terrain
   * in a different pass that breaks it.
   *
   * The fix is to stop compressing these particular bodies: positioned at
   * true scale and parented into `nearRoot` (which `Game` adds to the WORLD
   * scene, not the astronomical one), they land in whichever world pass their
   * real distance falls into and are depth-tested against the promoted
   * sibling's real terrain like any other object in that scene — ordinary
   * GPU depth comparison replaces the pass-order assumption, so whichever one
   * is actually nearer wins, pixel by pixel. `nearCutoffDistance` (the world
   * passes' current far plane) keeps this from silently hiding a body that
   * has drifted out of the far pass's frustum — beyond it, the body falls
   * back to the compressed path exactly as before, which is always visible
   * regardless of true distance.
   */
  update(
    cameraPositionRoot: Vec3,
    t: number,
    sunIntensityAt: (distance: number) => number,
    nearGroupIds: ReadonlySet<string> = EMPTY_SET,
    nearCutoffDistance = Infinity,
  ): void {
    const starPos = this.system.frames.stateInRoot(this.system.star.inertialFrameId, t).position;
    this.points.begin();

    for (const visual of this.visuals.values()) {
      const body = visual.body;

      const bodyPos = this.system.frames.stateInRoot(body.inertialFrameId, t).position;
      Vec3.subVectors(bodyPos, cameraPositionRoot, this.scratch);
      const trueDistance = this.scratch.length();
      if (trueDistance < MIN_BODY_DISTANCE) {
        visual.mesh.visible = false;
        if (visual.rings) visual.rings.mesh.visible = false;
        continue;
      }

      // A promoted body hides its impostor only once its real terrain is
      // actually within the world passes' far plane. The camera can be on a
      // moon whose parent planet is promoted (within 12 of its own radii) yet
      // sits beyond `nearCutoffDistance` — the world far plane is sized to the
      // view body's radius, so the planet's true-scale terrain is never drawn
      // there. Hiding the impostor would leave nothing at all; keep it on the
      // compressed path until the far plane actually reaches the terrain.
      const hide = this.hiddenBodyIds.has(body.id) && trueDistance - body.radius < nearCutoffDistance;
      visual.mesh.visible = !hide;
      if (visual.rings) visual.rings.mesh.visible = !hide;
      if (hide) continue;

      const dir = this.scratch.mulScalar(1 / trueDistance);
      const useTrueScale = nearGroupIds.has(body.id) && trueDistance < nearCutoffDistance;
      this.attach(visual, useTrueScale ? this.nearRoot : this.root);

      const renderDistance = useTrueScale ? trueDistance : compressDistance(trueDistance);
      const scale = renderDistance / trueDistance;

      visual.mesh.position.set(dir.x * renderDistance, dir.y * renderDistance, dir.z * renderDistance);
      // Turn the impostor with the planet. Object space is then the
      // planet-fixed frame, which is what the shader's 'vLocal' samples its
      // continents, weather and night lights in — without this they hang in
      // inertial space and the world visibly spins underneath its own
      // features, and the cloud field cannot line up with the real deck.
      const spin = this.system.frames.stateInRoot(body.fixedFrameId, t).orientation;
      visual.mesh.quaternion.set(spin.x, spin.y, spin.z, spin.w);
      visual.material.uniforms.uTime.value = t;
      // Same scale factor on the radius: angular size is preserved exactly
      // (trivially so at true scale, where it is 1).
      const renderRadius = Math.max(MIN_RENDER_RADIUS, body.radius * scale);
      visual.mesh.scale.setScalar(renderRadius);

      // Sun direction at this body, in the render frame.
      const toStar = Vec3.subVectors(starPos, bodyPos);
      const starDistance = toStar.length();
      const sunDir = starDistance > 0 ? toStar.mulScalar(1 / starDistance) : new Vec3(1, 0, 0);
      visual.material.uniforms.uSunDirection.value.set(sunDir.x, sunDir.y, sunDir.z);
      visual.material.uniforms.uSunIntensity.value = sunIntensityAt(starDistance);
      visual.rings?.setLighting(sunDir, visual.material.uniforms.uSunColor.value, visual.material.uniforms.uSunIntensity.value);

      if (visual.rings && body.def.rings) {
        visual.rings.mesh.position.copy(visual.mesh.position);
        const ringScale = (body.def.rings.innerRadius / body.radius) * renderRadius;
        visual.rings.mesh.scale.setScalar(ringScale);
      }

      // The star lights itself, and does so far above white. Its disc has to
      // clip as hard as the glare around it, or the two meet at a visible edge
      // and the star reads as a pale ball sitting inside a separate halo
      // instead of as one source too bright to look at.
      if (body === this.system.star) {
        visual.material.uniforms.uSunIntensity.value = STAR_SELF_INTENSITY;
        // The star has SunGlare, which is a far better model of a source this
        // bright than a point sprite. Nothing here applies to it.
        continue;
      }
      // A true-scale body is a close sibling by construction, so its impostor
      // is a resolved disc and the cross-fade below would return zero anyway —
      // and its position lies far outside the astronomical camera's frustum,
      // where a point in THIS scene could not be drawn correctly at all.
      if (useTrueScale) continue;

      // --- Point of light, for anything too far to resolve -------------------
      // Cross-fade first: below a pixel it is a point, above a couple it is a
      // disc, and drawing both strongly at once is a visible double image.
      const photometry = visual.photometry;
      const angularRadius = body.radius / trueDistance;
      const pointFade = 1 - smoothstep(POINT_ANGULAR_RADIUS, DISC_ANGULAR_RADIUS, angularRadius);
      // Phase: the sunlit fraction of the hemisphere facing the camera. The
      // phase angle is between "body to star" and "body to camera" (which is
      // -dir), so this is 1 at full phase and 0 at new, exactly as a planet
      // near conjunction should vanish.
      const phase = 0.5 * (1 - dir.dot(sunDir));
      const starIrradiance = this.starLuminosity / (4 * Math.PI * starDistance * starDistance);
      const flux =
        (visual.pointAlbedo * starIrradiance * body.radius * body.radius * phase) /
        (trueDistance * trueDistance);
      const magnitude = flux > MIN_POINT_FLUX ? -2.5 * Math.log10(flux) : Infinity;
      const vis = clamp01((MAG_LIMIT - magnitude) / (MAG_LIMIT - MAG_FULL));
      photometry.angularRadius = angularRadius;
      photometry.phase = phase;
      photometry.flux = flux;
      photometry.magnitude = magnitude;
      photometry.visibility = vis;
      photometry.pointFade = pointFade;
      if (vis <= 0 || pointFade <= 0) continue;

      // A brighter point is bigger AND hotter: an eye saturates around a bright
      // source, so it spreads and loses its hue, while a faint one stays small
      // and keeps its colour. That difference is most of what separates
      // "obviously a planet" from "one more star".
      this.pointColor.copy(visual.pointColor).lerp(WHITE, vis * POINT_WHITE_MIX);
      this.points.add(
        visual.mesh.position.x,
        visual.mesh.position.y,
        visual.mesh.position.z,
        this.pointColor.r,
        this.pointColor.g,
        this.pointColor.b,
        POINT_SIZE_MIN_PX + vis * POINT_SIZE_RANGE_PX,
        POINT_INTENSITY_GAIN * Math.pow(vis, POINT_INTENSITY_POWER) * pointFade,
      );
    }

    this.points.commit();
  }

  /** Reparents a body's mesh (and rings, if any) only when it actually moved. */
  private attach(visual: BodyVisual, target: THREE.Group): void {
    if (visual.mesh.parent !== target) target.add(visual.mesh);
    if (visual.rings && visual.rings.mesh.parent !== target) target.add(visual.rings.mesh);
  }

  /** Where the star sits in render coordinates, for the glare to attach to. */
  starRenderPosition(cameraPositionRoot: Vec3, t: number, out = new Vec3()): Vec3 {
    const starPos = this.system.frames.stateInRoot(this.system.star.inertialFrameId, t).position;
    Vec3.subVectors(starPos, cameraPositionRoot, out);
    const trueDistance = out.length();
    if (trueDistance < MIN_BODY_DISTANCE) return out.set(0, 0, 0);
    return out.mulScalar(compressDistance(trueDistance) / trueDistance);
  }

  setStarColor(color: THREE.Color): void {
    for (const v of this.visuals.values()) {
      v.material.uniforms.uSunColor.value.copy(color);
    }
  }

  /** Render position of a body this frame, for HUD markers. */
  renderPositionOf(bodyId: string): THREE.Vector3 | null {
    const v = this.visuals.get(bodyId);
    return v && v.mesh.visible ? v.mesh.position : null;
  }

  dispose(): void {
    this.sphere.dispose();
    this.points.dispose();
    for (const v of this.visuals.values()) {
      v.material.dispose();
      v.rings?.dispose();
    }
    this.visuals.clear();
  }
}
